import { useLockedAction } from "@components/plan-subscription-lock";
import DialogBase from "@components/ui/dialog-base";
import Spinner from "@components/ui/spinner";
import Switch from "@components/ui/switch";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { type KeyboardEventHandler, useState } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { track } from "@/features/analytics";
import {
  useCreateFolderMutation,
  useGetCurrentUserQuery,
} from "@/shared/services";
import { type IStoriesFolder, StoriesFolderUserPermEnum } from "@/shared/types";

const DIALOG_ID = "create-stories-folder-dialog";

interface IProps {
  afterCreate?: (folder: IStoriesFolder) => Promise<void>;
}

export const CreateStoriesFolderDialog = ({ afterCreate }: IProps) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState("");
  const { data: currentUser } = useGetCurrentUserQuery();

  const { plan } = useLockedAction({
    feature: "stories-folders-limit",
  });

  const [isOpenAccess, setIsOpenAccess] = useState(true);

  const { mutateAsync: createFolder, isPending: isLoading } =
    useCreateFolderMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (!name?.trim() || isLoading || !currentUser) return;

    try {
      const resp = await createFolder({
        name: name.trim(),
        openAccess: isOpenAccess,
        users: [
          {
            oktaId: currentUser.oktaId,
            status: StoriesFolderUserPermEnum.Owner,
          },
        ],
      });
      if (afterCreate && resp) {
        await afterCreate(resp);
      }
    } catch (e) {
      track("error creating new folder", {
        newFolderName: name.trim(),
        error: e,
      });
      toast.error("Error creating new folder");
    }

    handleClose();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <DialogBase>
      <DialogBase.Header title="New folder" onClose={handleClose} />
      <DialogBase.Content>
        <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
          Name
        </span>
        <Input
          placeholder="Add your folder name here..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        {plan === "team" && (
          <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-blue-50 tw-p-4">
            <span className="tw-text-[11px] tw-text-blue-800">
              Share with everyone in your organization
            </span>
            <Switch.Toggle
              checked={isOpenAccess}
              onCheckedChange={setIsOpenAccess}
            />
          </div>
        )}
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name?.trim()}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Add folder"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

CreateStoriesFolderDialog.dialogId = DIALOG_ID;
